// Applying a camo to a unit sprite, the way MegaMek does it.
//
// Ported from MegaMek 0.51.0 EntityImage.applyColor / rotatedAndScaledColor.
// The rules that matter:
//
//   * A sprite is 84x72 (HexTileset.HEX_W by HEX_H).
//   * Camo is applied only where the sprite pixel is grey - red == green ==
//     blue - and not transparent. Coloured pixels are left exactly as they are,
//     which is how cockpit glass and unit markings survive.
//   * The final colour is the camo colour multiplied by the sprite's grey
//     level: `camo * grey / 255`. So the palette a player picks is a ceiling,
//     not the colour that appears; shadowed parts of the unit go much darker.
//   * The camo is sampled about the sprite's centre with bilinear
//     interpolation, optionally rotated and scaled.
//
// We author camo at exactly 84x72 so that this sampling is the identity at
// scale 1. That removes the one thing this port cannot verify from source -
// whether MegaMek crops, scales or tiles a camo image that is not sprite-sized
// before it gets here - because at exactly sprite size every answer is the
// same. Uploads are fitted to 84x72 for the same reason.
//
// Not ported: the facing-dependent shadow/highlight overlays, which MegaMek
// applies only when its camo-overlay preference is on. They darken edges
// slightly and do not change the character of a pattern.

export const SPRITE_W = 84;
export const SPRITE_H = 72;

export const CAMO_W = SPRITE_W;
export const CAMO_H = SPRITE_H;

const CX = 41.5; // SPRITE_W / 2 - 0.5
const CY = 35.5; // SPRITE_H / 2 - 0.5

/** Mirror an out-of-range coordinate back into the image, as MegaMek does. */
function mirror(v: number, max: number): number {
  if (max <= 1) return 0;
  const period = 2 * max;
  let t = ((v % period) + period) % period;
  if (t >= max) t = period - t - 1;
  return t;
}

function sampleBilinear(
  camo: Uint8ClampedArray,
  w: number,
  h: number,
  x: number,
  y: number,
): [number, number, number] {
  const x0 = Math.floor(x);
  const y0 = Math.floor(y);
  const fx = x - x0;
  const fy = y - y0;
  const xa = mirror(x0, w);
  const xb = mirror(x0 + 1, w);
  const ya = mirror(y0, h);
  const yb = mirror(y0 + 1, h);
  const out: [number, number, number] = [0, 0, 0];
  for (let c = 0; c < 3; c++) {
    const p00 = camo[(ya * w + xa) * 4 + c]!;
    const p10 = camo[(ya * w + xb) * 4 + c]!;
    const p01 = camo[(yb * w + xa) * 4 + c]!;
    const p11 = camo[(yb * w + xb) * 4 + c]!;
    const top = p00 + (p10 - p00) * fx;
    const bot = p01 + (p11 - p01) * fx;
    out[c] = top + (bot - top) * fy;
  }
  return out;
}

export type CamoOptions = { rotation?: number; scale?: number };

/**
 * Composite a camo onto a sprite. Returns a new ImageData; neither input is
 * modified, so this is safe to call on every keystroke.
 */
export function applyCamo(
  sprite: ImageData,
  camo: ImageData,
  opts: CamoOptions = {},
): ImageData {
  const rotation = opts.rotation ?? 0;
  const scale = opts.scale ?? 1;
  const out = new ImageData(sprite.width, sprite.height);
  const src = sprite.data;
  const dst = out.data;
  const cam = camo.data;
  const sin = Math.sin(rotation);
  const cos = Math.cos(rotation);

  for (let y = 0; y < sprite.height; y++) {
    for (let x = 0; x < sprite.width; x++) {
      const i = (y * sprite.width + x) * 4;
      const r = src[i]!;
      const g = src[i + 1]!;
      const b = src[i + 2]!;
      const a = src[i + 3]!;

      dst[i] = r;
      dst[i + 1] = g;
      dst[i + 2] = b;
      dst[i + 3] = a;

      // Only grey, only opaque. Everything else is the artist's, not ours.
      if (a === 0 || !(r === g && g === b)) continue;

      const cx = x - CX;
      const cy = y - CY;
      const rx = (cos * cx - sin * cy) / scale + CX;
      const ry = (sin * cx + cos * cy) / scale + CY;
      const [cr, cg, cb] = sampleBilinear(cam, camo.width, camo.height, rx, ry);

      // The grey level scales the camo colour. This is the whole trick: the
      // sprite carries the shading, the camo carries the hue.
      const grey = b;
      dst[i] = (cr * grey) / 255;
      dst[i + 1] = (cg * grey) / 255;
      dst[i + 2] = (cb * grey) / 255;
    }
  }
  return out;
}

// --- preview sprites -------------------------------------------------------
//
// Real MegaMek unit art, taken from the 0.51.0 release and used under
// CC BY-NC-SA 4.0 - the licence the whole `data` tree ships under. This
// project is non-commercial and published, which is what that licence asks
// for; the attribution is in the editor UI and in the repo README.
//
// Every sprite in the release is exactly 84x72 (checked: 6986 of them), and so
// is nearly every stock camo (1681 of 1684). Authoring at that size is
// therefore not our convention imposed on MegaMek, it is MegaMek's own.
//
// Picked to span what a camo has to survive. Four mek weight classes including
// a quad, plus every other shape a camo lands on, and - the point of the set -
// a twelve-fold range of size: 363 non-transparent pixels on the Roc against
// 4578 on the Union. A pattern that works on both works everywhere.
//
// Those counts use MegaMek's own test, alpha != 0, not a half-alpha cutoff.
// The difference is not academic: the Union's anti-aliased shadow ring is
// semi-transparent and coloured, so by MegaMek's rule only 58% of its
// non-transparent pixels take camo at all, against 97-100% for most of the
// others. Measuring it the other way makes it look like a clean 100%.
//
// The aero is a compromise the numbers force. Fighter sprites are mostly
// coloured rather than grey - the whole class averages 40% - so camo shows on
// less of them than on anything else here. The one fighter at 99% grey is the
// Star Dagger, which nobody has heard of. The Chippewa is 36% grey but is
// House Steiner's main heavy fighter, and its wide swept wing gives a camo
// more area than any other fighter in the release.

// Served rather than committed, like the scenario preview's art: these are the
// same files, at the path they have inside the release, and `unitSprite` turns
// that into an address on this site. Same origin as the page, which is what
// keeps `getImageData` below legal - a cross-origin image taints the canvas
// and every read off it throws.
const pantherUrl = unitSprite("meks/Panther.png");
const scorpionUrl = unitSprite("meks/Scorpion.png");
const timberwolfUrl = unitSprite("meks/MadCat.png");
const atlasUrl = unitSprite("meks/atlas.png");
const demolisherUrl = unitSprite("vehicles/Demolisher.png");
const chippewaUrl = unitSprite("fighter/Chippewa.png");
const elementalUrl = unitSprite("battle armor/Elemental_5.png");
const rocUrl = unitSprite("protomeks/Roc.png");
const unionUrl = unitSprite("dropships/Union.png");

export type SpriteDef = {
  id: string;
  name: string;
  /** Short subtitle shown under the name. */
  kind: string;
  /** The full description, for the tooltip. */
  detail: string;
  url: string;
};

export const PREVIEW_SPRITES: SpriteDef[] = [
  {
    id: "panther",
    name: "Panther",
    kind: "Light Mek",
    detail: "Panther - light BattleMek, 35 tons",
    url: pantherUrl,
  },
  {
    id: "scorpion",
    name: "Scorpion",
    kind: "Medium Mek \u00b7 quad",
    detail: "Scorpion - medium BattleMek, 55 tons, four-legged",
    url: scorpionUrl,
  },
  {
    id: "timberwolf",
    name: "Timber Wolf",
    kind: "Heavy OmniMek",
    detail:
      "Timber Wolf - heavy OmniMek, 75 tons. Inner Sphere codename Mad Cat",
    url: timberwolfUrl,
  },
  {
    id: "atlas",
    name: "Atlas",
    kind: "Assault Mek",
    detail: "Atlas - assault BattleMek, 100 tons",
    url: atlasUrl,
  },
  {
    id: "demolisher",
    name: "Demolisher",
    kind: "Vehicle",
    detail: "Demolisher - heavy tracked assault vehicle",
    url: demolisherUrl,
  },
  {
    id: "chippewa",
    name: "Chippewa",
    kind: "Aerospace",
    detail: "Chippewa - heavy aerospace fighter, 90 tons",
    url: chippewaUrl,
  },
  {
    id: "elemental",
    name: "Elemental",
    kind: "Battle Armor",
    detail: "Elemental - Clan battle armour, a Point of five",
    url: elementalUrl,
  },
  {
    id: "roc",
    name: "Roc",
    kind: "ProtoMek",
    detail: "Roc - ProtoMek, 7 tons, Clan Smoke Jaguar",
    url: rocUrl,
  },
  {
    id: "union",
    name: "Union",
    kind: "DropShip",
    detail: "Union - spheroid DropShip, 3600 tons",
    url: unionUrl,
  },
];

// --- backgrounds -----------------------------------------------------------
//
// A camo is never seen against nothing, so the preview should not show it that
// way: a pattern that reads beautifully on a dark page can vanish on snow.
// These are the hexes a match actually draws, at the size it draws them.
//
// The default is our own board tile from arena's skin rather than a MegaMek
// terrain - it is the neutral one, and it is what a lobby looks like. Rough is
// composited here rather than shipped whole, because MegaMek draws rough as a
// transparent overlay on top of whatever ground the hex already has.

import { unitSprite } from "../megamek";

import battlespaceUrl from "./backgrounds/battlespace.png?url";
import grassUrl from "./backgrounds/grass.png?url";
import roughUrl from "./backgrounds/rough.png?url";
import snowUrl from "./backgrounds/snow.png?url";

export type BackgroundDef = { id: string; name: string; url: string };

export const BACKGROUNDS: BackgroundDef[] = [
  { id: "battlespace", name: "Board", url: battlespaceUrl },
  { id: "grass", name: "Grass", url: grassUrl },
  { id: "rough", name: "Rough", url: roughUrl },
  { id: "snow", name: "Snow", url: snowUrl },
];

/** Decode one 84x72 image to pixels. Exported for the scenario preview,
 * which draws the same sprites-on-hexes composition this module's callers
 * do. */
export async function loadTile(url: string): Promise<ImageData> {
  const img = await new Promise<HTMLImageElement>((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error(`could not load ${url}`));
    image.src = url;
  });
  const canvas = document.createElement("canvas");
  canvas.width = SPRITE_W;
  canvas.height = SPRITE_H;
  const ctx = canvas.getContext("2d")!;
  ctx.drawImage(img, 0, 0);
  return ctx.getImageData(0, 0, SPRITE_W, SPRITE_H);
}

export async function loadSprites(): Promise<Map<string, ImageData>> {
  const pairs = await Promise.all(
    PREVIEW_SPRITES.map(async (s) => [s.id, await loadTile(s.url)] as const),
  );
  return new Map(pairs);
}

export async function loadBackgrounds(): Promise<Map<string, ImageData>> {
  const pairs = await Promise.all(
    BACKGROUNDS.map(async (b) => [b.id, await loadTile(b.url)] as const),
  );
  return new Map(pairs);
}

/** Draw `fg` over `bg`, both sprite-sized. Straight alpha, no blending modes. */
export function over(bg: ImageData | undefined, fg: ImageData): ImageData {
  if (!bg) return fg;
  const out = new ImageData(fg.width, fg.height);
  for (let i = 0; i < fg.data.length; i += 4) {
    const a = fg.data[i + 3]! / 255;
    for (let c = 0; c < 3; c++) {
      out.data[i + c] = fg.data[i + c]! * a + bg.data[i + c]! * (1 - a);
    }
    out.data[i + 3] = 255;
  }
  return out;
}

/**
 * Fit an arbitrary image into the camo canvas. `zoom` above 1 crops tighter,
 * and the offsets slide the crop window; this is the only place a framing
 * control earns its keep, because an imported photograph has a subject and the
 * centre of the file is not always it.
 */
export function fitToCamo(
  source: CanvasImageSource,
  sw: number,
  sh: number,
  zoom = 1,
  offsetX = 0,
  offsetY = 0,
): ImageData {
  const canvas = document.createElement("canvas");
  canvas.width = CAMO_W;
  canvas.height = CAMO_H;
  const ctx = canvas.getContext("2d")!;
  ctx.imageSmoothingQuality = "high";
  const scale = Math.max(CAMO_W / sw, CAMO_H / sh) * zoom;
  const dw = sw * scale;
  const dh = sh * scale;
  ctx.drawImage(
    source,
    (CAMO_W - dw) / 2 + offsetX * (dw - CAMO_W) * 0.5,
    (CAMO_H - dh) / 2 + offsetY * (dh - CAMO_H) * 0.5,
    dw,
    dh,
  );
  return ctx.getImageData(0, 0, CAMO_W, CAMO_H);
}
